/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { internalSessionIssuersSetEnabled } from "../funcs/internalSessionIssuersSetEnabled.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { InternalSessionIssuer } from "../models/components/internalsessionissuer.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetInternalSessionIssuerEnabledRequest,
  SetInternalSessionIssuerEnabledSecurity,
} from "../models/operations/setinternalsessionissuerenabled.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SetInternalSessionIssuerEnabledMutationVariables = {
  request: SetInternalSessionIssuerEnabledRequest;
  security?: SetInternalSessionIssuerEnabledSecurity | undefined;
  options?: RequestOptions;
};

export type SetInternalSessionIssuerEnabledMutationData = InternalSessionIssuer;

export type SetInternalSessionIssuerEnabledMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setEnabled internalSessionIssuers
 *
 * @remarks
 * Enable or disable signing with the organization's internal session issuer. Enabling provisions the issuer with a dedicated Speakeasy-managed signing key the first time; it does not configure trust at any service. Disabling keeps the issuer URI and its published keys. Requires org:admin; enabling also needs a verified email domain and the issuer rollout, while disabling never does.
 */
export function useSetInternalSessionIssuerEnabledMutation(
  options?: MutationHookOptions<
    SetInternalSessionIssuerEnabledMutationData,
    SetInternalSessionIssuerEnabledMutationError,
    SetInternalSessionIssuerEnabledMutationVariables
  >,
): UseMutationResult<
  SetInternalSessionIssuerEnabledMutationData,
  SetInternalSessionIssuerEnabledMutationError,
  SetInternalSessionIssuerEnabledMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSetInternalSessionIssuerEnabledMutation(client, options),
    ...options,
  });
}

export function mutationKeySetInternalSessionIssuerEnabled(): MutationKey {
  return ["@gram/client", "internalSessionIssuers", "setEnabled"];
}

export function buildSetInternalSessionIssuerEnabledMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SetInternalSessionIssuerEnabledMutationVariables,
  ) => Promise<SetInternalSessionIssuerEnabledMutationData>;
} {
  return {
    mutationKey: mutationKeySetInternalSessionIssuerEnabled(),
    mutationFn: function setInternalSessionIssuerEnabledMutationFn({
      request,
      security,
      options,
    }): Promise<SetInternalSessionIssuerEnabledMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(internalSessionIssuersSetEnabled(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
